import React, {useContext, useEffect} from "react";
import {Route, Routes, Navigate} from "react-router-dom";
import {
    Error,
    ForgotPasswordPage,
    MainLayout,
    LoginPage,
    PrivateRoutes,
    Setting,
    Attendance,
    JanusP2PCall,
    User,
    EmployeeLeave,
    LeaveSummary,
    LeaveRequest,
    Dashboard,
    LeaveApplication,
    UserCreateSuccess,
    LeavesType,
    Designation,
    WorkWeek,
    Qualification,
    LeaveEntitlement,
    AttendanceStats,
    MediaHub,
    Events,
    PolicyPage
} from "./component/index";
import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import ApiInterceptor from "./apiInterceptor";
import {ToastContainer} from "react-toastify";
import UserLoginContext from "./context/userLoginContext";
import ResetPassword from "./component/user/reset-password/reset-password";
import EmployeeBatchUpload from "./component/user/upload-employees-file";
import ChatLayout from "./component/chat/chat-layout";
import OneToOneChat from "./component/chat/oneToOneChat";
import {PunchProvider} from "./context/punchContext";
import AccessDenied from "./pages/error-pages/access-denied-error";
import EmployeeCreate from "./page-components/employee/create/index";
import SomethingWentWrong from "./pages/error-pages/something-went-wrong-error";
import LeavePeriod from "./component/setting/setting-table/leavePeriod";
import HolidayPage from "./page-components/holiday/holiday";
import SearchUsage from "./page-components/common/search-usage";
import EmployeeSearch from "./page-components/employee/search/employee-search";
import CelebrationPage from "./page-components/celebrations/celebrations";
import EmployeeDetail from './page-components/employee-detail/employee-detail';
import MyNotesPage from "./pages/my-notes-page";
import NoInternetPage from "./pages/error-pages/no-internet-page";
import useOnlineStatus from "./pages/useNetworkStatus";
import {initTokenRefresh} from "./services/tokenService";
import {SearchProvider} from "./platform/search/provider/search-provider";
import AssetCategory from "./page-components/asset/asset-category/asset-category";
import AssetDashboard from "./page-components/asset/asset-dashboard/asset-dashboard";
import AssetAllocationAndRequest from "./page-components/asset/asset-allocation-request/allocation-request-navbar";
import AssetAllocation from "./page-components/asset/asset-allocation-request/allocate/asset-allocation";
import AssetRequest from "./page-components/asset/asset-allocation-request/requests/asset-request";
import AssetInventory from "./page-components/asset/asset-inventory/asset-inventory";
import OverdueAssets from "./page-components/asset/asset-allocation-request/overdue/asset-overdue";
import MediaCollection from "./page-components/media-hub/component/media-collection/media-collection";
import {webSocketService} from "./services/WebSocketService";
import Poll from "./page-components/poll-management/poll";
import '@ant-design/v5-patch-for-react-19';
import {AttendanceSearchProvider} from "./page-components/attendance/AttendanceContext";


const App = () => {
    const {user} = useContext(UserLoginContext);
    const isOnline = useOnlineStatus();

    useEffect(() => {
        initTokenRefresh();
    }, []);

    useEffect(() => {
        if (!user.id) return;
        webSocketService.connect();
        return () => {
            webSocketService.disconnect();
        };
    }, [user]);

    if (!isOnline) {
        return <NoInternetPage/>;
    }

    return (

        <SearchProvider userRoles={[...user.roles]}>
            <>
                <ApiInterceptor/>
                <div className="App">
                    <ToastContainer
                        autoClose={8000}
                        position="bottom-right"
                    />
                    <Routes>
                        <Route path="/" element={<LoginPage/>}/>
                        <Route
                            path="/login"
                            element={
                                user.id
                                    ? <Navigate to="/dashboard" replace/>
                                    : <LoginPage/>
                            }
                        />
                        <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>


                        <Route element={<PrivateRoutes/>}>
                            <Route path="/" element={<MainLayout/>}>
                                {user?.roles.length !== 0 &&
                                <Route index element={
                                    <PunchProvider><Dashboard/></PunchProvider>}/>}
                                <Route path="dashboard" element={
                                    <PunchProvider><Dashboard/></PunchProvider>}/>

                                <Route path=":id/list" element={<User/>}/>
                                <Route path="employee/create/:section" element={<EmployeeCreate/>}/>
                                <Route path="employee/edit/:section" element={<EmployeeCreate/>}/>

                                <Route path="user/create-success" element={<UserCreateSuccess/>}/>
                                <Route path="user/reset-password" element={<ResetPassword/>}/>
                                <Route path="employee/search" element={<EmployeeSearch/>}/>
                                <Route path="search-usage" element={<SearchUsage/>}/>
                                <Route path="employee/attendance"
                                       element={
                                           <AttendanceSearchProvider><PunchProvider><Attendance/></PunchProvider></AttendanceSearchProvider>}/>
                                <Route path="employee/attendance/stats" element={<AttendanceStats/>}/>
                                <Route path="my/attendance"
                                       element={<AttendanceSearchProvider><Attendance/></AttendanceSearchProvider>}/>
                                <Route path="my/attendance/stats" element={<AttendanceStats/>}/>
                                <Route path={'highlights/celebrations'} element={<CelebrationPage/>}/>
                                <Route path={'highlights/our-events'} element={<Events/>}/>

                                <Route path={"highlights/media-hub"} element={<MediaHub/>}>
                                    <Route element={<PrivateRoutes/>}>
                                        <Route element={<PrivateRoutes/>}>
                                            <Route path=":tabName" element={<MediaHub/>}/>
                                        </Route>
                                        <Route element={<PrivateRoutes/>}>
                                            <Route path={":tabName/:folderName"} element={<MediaCollection/>}/>
                                        </Route>

                                    </Route>
                                </Route>

                                <Route path="my-notes" element={<MyNotesPage/>}/>
                                <Route path="employee/detail" element={<EmployeeDetail/>}/>
                                <Route path="my-profile" element={<EmployeeDetail/>}/>
                                <Route path="policy" element={<PolicyPage/>}/>
                                <Route path="poll" element={<Poll/>}/>

                                <Route path="setting" element={<Setting/>}>
                                    <Route element={<PrivateRoutes/>}>
                                        <Route path="leave-types" element={<LeavesType/>}/>
                                        <Route path="designation" element={<Designation/>}/>
                                        <Route path="work-week" element={<WorkWeek/>}/>
                                        <Route path="qualification" element={<Qualification/>}/>
                                        <Route path="leave-period" element={<LeavePeriod/>}/>
                                    </Route>
                                </Route>

                                <Route path={"time-off/leaves"} element={<EmployeeLeave/>}>
                                    <Route element={<PrivateRoutes/>}>
                                        <Route path="summary" element={<LeaveSummary/>}/>
                                        <Route path="entitlement" element={<LeaveEntitlement/>}/>
                                        <Route path="request" element={<LeaveRequest/>}/>
                                        <Route path="approvals" element={<LeaveApplication/>}/>
                                        <Route path="team-summary" element={<LeaveSummary/>}/>
                                    </Route>
                                </Route>

                                <Route path={"asset/dashboard"} element={<AssetDashboard/>}/>
                                <Route path={"asset/category"} element={<AssetCategory/>}/>
                                <Route path={"asset/inventory"} element={<AssetInventory/>}/>


                                <Route element={<AssetAllocationAndRequest/>}>
                                    <Route element={<PrivateRoutes/>}>
                                        <Route path="asset/allocation" element={<AssetAllocation/>}/>
                                        <Route path="asset/request" element={<AssetRequest/>}/>
                                        <Route path="asset/overdue" element={<OverdueAssets/>}/>
                                    </Route>
                                </Route>

                                <Route path={"time-off/holidays"} element={<HolidayPage/>}/>
                                <Route path="user/batch-upload" element={<EmployeeBatchUpload/>}/>
                                <Route path="leaves" element={<EmployeeLeave/>}/>
                                <Route path={"meeting"} element={<JanusP2PCall/>}/>
                                <Route path={"chat"} element={<ChatLayout/>}/>
                                <Route path={"chat/:id"} element={<OneToOneChat/>}/>
                                <Route path={"chat/:id"} element={<OneToOneChat/>}/>

                            </Route>

                            {/* Protected error pages */}
                            <Route path="*" element={<Error/>}/>
                            <Route path={"access-denied"} element={<AccessDenied/>}/>
                            <Route path={"something-wrong"} element={<SomethingWentWrong/>}/>
                            <Route path={"error"} element={<Error/>}/>
                        </Route>


                    </Routes>
                </div>
            </>
        </SearchProvider>

    )
}

export default App;
